import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ContentLoader,
  List,
  Notification,
  StatusCard,
} from 'shared/components';
import { displayValue, formatDate } from 'shared/helpers';
import { fetchLicenseUserGroups } from '../../licenses/actions';

const PAGE_SIZE = 10;

const LicenseUserGroups = ({ licenseUserId }) => {
  const { t } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [error, setError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);

  const getGroups = useCallback(
    async (page) => {
      setIsLoading(true);
      try {
        const response = await fetchLicenseUserGroups(
          licenseUserId,
          undefined,
          page,
          PAGE_SIZE,
        );
        setGroups(get(response, 'data.results', []));
        setCount(get(response, 'data.count', 0));
      } catch (error) {
        Notification(
          'error',
          t('Error occurred'),
          t('There was an error while getting user groups'),
        );
        setError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [licenseUserId],
  );

  useEffect(() => {
    if (licenseUserId) {
      getGroups(currentPage);
    }
  }, [licenseUserId, currentPage, getGroups, retryToken]);

  const handleRetry = () => {
    setRetryToken((token) => token + 1);
    setError(false);
  };

  const columns = [
    {
      Header: t('Created on'),
      accessor: 'created_at',
      Cell: (cellInfo) => formatDate(cellInfo.value),
      width: 140,
      sortable: false,
    },
    {
      Header: t('Group Name'),
      accessor: 'name',
      Cell: (cellData) => displayValue(cellData.value),
      width: 250,
      sortable: false,
    },
    {
      Header: t('Description'),
      accessor: 'description',
      Cell: (cellData) => displayValue(cellData.value),
      sortable: false,
    },
    {
      Header: t('Customer Account'),
      accessor: 'customer_account',
      Cell: (cellData) => displayValue(cellData.value),
      width: 200,
      sortable: false,
    },
    {
      Header: t('Number of Users'),
      id: 'number_of_users',
      className: 'text-center',
      headerClassName: 'text-center',
      accessor: (row) => get(row, 'license_users', []).length,
      Cell: (cellData) => displayValue(cellData.value, 0),
      width: 140,
      sortable: false,
    },
    {
      Header: t('Status'),
      id: 'status',
      headerClassName: 'text-center',
      className: 'text-center',
      Cell: () => <StatusCard status="success" text={t('Active')} />,
      width: 110,
      sortable: false,
    },
  ];

  if (isLoading) {
    return <ContentLoader text={t('Loading user groups')} />;
  }

  if (error) {
    return (
      <div className="LicenseUserDetails-error">
        <p>{t('Unable to load user groups for this user')}</p>
        <Button theme="info" size="sm" onClick={handleRetry}>
          {t('Retry')}
        </Button>
      </div>
    );
  }

  if (!groups.length) {
    return (
      <div className="LicenseUserDetails-empty">
        {t('No user groups for this user')}
      </div>
    );
  }

  return (
    <div className="LicenseUserDetails-list">
      <List
        data={groups}
        columns={columns}
        minRows={2}
        loading={isLoading}
        manual
        page={currentPage}
        pages={Math.ceil(count / PAGE_SIZE)}
        showPagination={count > PAGE_SIZE}
        onPageChange={(page) => setCurrentPage(page)}
      />
    </div>
  );
};

LicenseUserGroups.propTypes = {
  licenseUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default LicenseUserGroups;
